/**
 * React Query hooks for Bedrock connection data fetching and mutations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBedrockSetupInstructions,
  listBedrockConnections,
  createBedrockConnection,
  deleteBedrockConnection,
  syncBedrockConnection,
} from "@/services/bedrockService";
import type { BedrockConnectionCreate } from "@/types/bedrock";

const QUERY_KEY = "bedrock-connections";

export function useBedrockConnections(page: number = 1, itemsPerPage: number = 10) {
  return useQuery({
    queryKey: [QUERY_KEY, page, itemsPerPage],
    queryFn: () => listBedrockConnections(page, itemsPerPage),
    staleTime: 30_000,
  });
}

export function useBedrockSetupInstructions(enabled: boolean) {
  return useQuery({
    queryKey: ["bedrock-setup-instructions"],
    queryFn: getBedrockSetupInstructions,
    enabled,
    staleTime: 5 * 60_000, // 5 minutes — external_id is stable for the session
  });
}

export function useCreateBedrockConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BedrockConnectionCreate) => createBedrockConnection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: "all" });
    },
  });
}

export function useDeleteBedrockConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: number) => deleteBedrockConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: "all" });
    },
  });
}

export function useSyncBedrockConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: number) => syncBedrockConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: "all" });
    },
  });
}
